import { useState } from 'react';
import { Coins, Infinity as InfinityIcon, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAiCreditsStatus } from '@/hooks/queries/useAiCredits';
import { AiCreditsPurchaseSheet } from './AiCreditsPurchaseSheet';

interface AiCreditsIndicatorProps {
  compact?: boolean;
}

export function AiCreditsIndicator({ compact = false }: AiCreditsIndicatorProps) {
  const { data: status, isLoading } = useAiCreditsStatus();
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  if (isLoading || !status) return null;

  // Mode illimité
  if (status.unlimited) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border border-secondary-600 bg-secondary-100 px-2 py-1 text-secondary-900',
          compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
        )}
        aria-label="Crédits illimités"
      >
        <InfinityIcon className={cn('shrink-0', compact ? 'size-3' : 'size-3.5')} />
        {!compact && <span className="text-xs font-semibold">Illimité</span>}
      </div>
    );
  }

  // Owner avec solde connu
  if (status.balance !== undefined) {
    const isExhausted = status.balance <= 0;
    const isLow = status.balance > 0 && status.balance <= 10;

    return (
      <>
        <button
          type="button"
          onClick={() => setPurchaseOpen(true)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-semibold transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
            isExhausted
              ? 'border-error-dark bg-error-light text-error-dark focus-visible:ring-error'
              : isLow
                ? 'border-warning-dark bg-warning-light text-warning-dark focus-visible:ring-warning-dark'
                : 'border-success-dark bg-success-light text-success-dark focus-visible:ring-success-dark',
          )}
          aria-label={`${status.balance} crédits IA disponibles — Acheter des crédits`}
        >
          <Coins className={cn('shrink-0', compact ? 'size-3' : 'size-3.5')} />
          {!compact && (
            <span>
              {status.balance} crédit{status.balance !== 1 ? 's' : ''}
            </span>
          )}
          {compact && <span>{status.balance}</span>}
        </button>

        <AiCreditsPurchaseSheet open={purchaseOpen} onOpenChange={setPurchaseOpen} />
      </>
    );
  }

  // Employé : statut disponible/épuisé
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-semibold',
        compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
        status.available
          ? 'border-success-dark bg-success-light text-success-dark'
          : 'border-error-dark bg-error-light text-error-dark',
      )}
      aria-label={status.available ? 'IA disponible' : 'Crédits épuisés'}
    >
      <Zap className={cn('shrink-0', compact ? 'size-3' : 'size-3.5')} />
      {!compact && <span>{status.available ? 'IA disponible' : 'Crédits épuisés'}</span>}
    </div>
  );
}
